<template>
  <BaseDialog centered contentClass="create-manual-quote-dialog" ref="dialog" :show-actions="true">
    <template slot="title">{{ isEditing ? $t('actions.edit') : $t('quotes.create-quote') }}</template>
    <template slot="content">
      <div class="create-manual-quote-dialog__body">
        <div class="create-manual-quote-dialog__section" v-if="potentialBidder">
          <div class="create-manual-quote-dialog__subtitle">
            {{ $t('quotes.linked-potential-bidder') }}
          </div>
          <BasePotentialBidder :potential-bidder="potentialBidder" :key="potentialBidder.id" />
        </div>

        <form @submit.prevent="onSubmit" @keydown.enter.prevent>
          <FormInput v-model="form.name" name="quoteName" :label="$t('page.admin-users.name')" hasGrayBackground />

          <FormInput
            v-model="form.amount"
            name="quoteAmount"
            type="number"
            :label="$t('quotes.manual-quote.amount')"
            hasGrayBackground
          />

          <FormInput
            v-model="form.description"
            name="quoteDescription"
            :label="$t('quotes.manual-quote.description')"
            isTextarea
            hasGrayBackground
          />

          <div class="create-manual-quote-dialog__subtitle">
            {{ $t('quotes.manual-quote.contact') }}
          </div>

          <div class="create-manual-quote-dialog__row">
            <FormInput
              v-model="form.contactFirstName"
              name="contactFirstName"
              :label="$t('quotes.manual-quote.first-name')"
              hasGrayBackground
            />
            <FormInput
              v-model="form.contactLastName"
              name="contactLastName"
              :label="$t('quotes.manual-quote.last-name')"
              hasGrayBackground
            />
          </div>

          <FormInput
            v-model="form.contactEmail"
            name="contactEmail"
            :label="$t('page.opportunities.potential-bidder-dialog.contact-email')"
            hasGrayBackground
          />

          <FormInput
            v-model="form.contactPhone"
            name="contactPhone"
            :label="$t('page.opportunities.potential-bidder-dialog.contact-phone')"
            hasGrayBackground
          />
        </form>
      </div>
    </template>

    <template slot="actions">
      <div class="create-manual-quote-dialog__actions">
        <FormButton is-outlined class="create-manual-quote-dialog__cancel" type="button" @click.native="onCancel">
          {{ $t('actions.cancel') }}
        </FormButton>
        <FormButton type="button" :disabled="isDisabled" @click.native="onSubmit">
          <span>{{ isEditing ? $t('actions.edit') : $t('quotes.create-quote') }}</span>
        </FormButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import BasePotentialBidder from '@/components/base/BasePotentialBidder';
import FormButton from '@/components/form/FormButton';
import FormInput from '@/components/form/FormInput';

import { mapActions, mapGetters } from 'vuex';
import { formatPhoneNumber } from '@/util/phone-util';

export default {
  name: 'CreateManualQuoteDialog',
  components: {
    BaseDialog,
    BasePotentialBidder,
    FormButton,
    FormInput,
  },
  data() {
    return {
      potentialBidder: null,
      editingQuote: null,
      form: {
        name: null,
        amount: null,
        description: null,
        contactFirstName: null,
        contactLastName: null,
        contactEmail: null,
        contactPhone: null,
      },
    };
  },
  computed: {
    ...mapGetters('opportunity', ['opportunity']),
    isEditing() {
      return !!this.editingQuote;
    },
    isDisabled() {
      return !this.form.name;
    },
  },
  methods: {
    ...mapActions('quotes', ['createQuote', 'updateQuote']),
    openDialog(potentialBidder) {
      this.editingQuote = null;
      this.potentialBidder = potentialBidder;
      this.prefillForm(potentialBidder);
      this.$refs.dialog.openModal();
    },
    editDialog(quote) {
      this.editingQuote = quote;
      this.potentialBidder = quote.potentialBidder || null;
      this.form = {
        name: quote.accountName || null,
        amount: quote.totalAmount || null,
        description: quote.description || null,
        contactFirstName: quote.contactFirstName || null,
        contactLastName: quote.contactLastName || null,
        contactEmail: quote.contactEmail || null,
        contactPhone: quote.contactPhone || null,
      };
      this.$refs.dialog.openModal();
    },
    prefillForm(potentialBidder) {
      if (!potentialBidder) return;

      this.form.name = potentialBidder.organization_name || null;
      const contact =
        potentialBidder.contact_points && potentialBidder.contact_points[0] ? potentialBidder.contact_points[0] : {};

      const fullName = contact.name || '';
      const nameParts = fullName.split(' ');

      this.form.contactFirstName = nameParts[0] || null;
      this.form.contactLastName = nameParts.slice(1).join(' ') || null;
      this.form.contactEmail = contact.email || null;
      this.form.contactPhone = contact.telephone ? formatPhoneNumber(contact.telephone) : null;

      this.form.amount = null;
      this.form.description = null;
    },
    onSubmit() {
      if (this.isEditing) {
        this.updateQuote({
          opportunityId: this.opportunity.id,
          quoteId: this.editingQuote.id,
          quote: {
            ...this.editingQuote,
            accountName: this.form.name,
            description: this.form.description,
            contactFirstName: this.form.contactFirstName,
            contactLastName: this.form.contactLastName,
            contactPhone: this.form.contactPhone,
            contactEmail: this.form.contactEmail,
            totalAmount: this.form.amount ? Number(this.form.amount) : null,
          },
        })
          .then(() => {
            this.$toast.success(this.$t('quotes.manual-quote.update-success'));
            this.$emit('quote-created');
            this.closeDialog();
          })
          .catch(() => {
            this.$toast.error(this.$t('quotes.manual-quote.create-error'));
          });
        return;
      }

      this.createQuote({
        opportunityId: this.opportunity.id,
        payload: {
          name: this.form.name,
          quoteNumber: null,
          description: this.form.description,
          accountName: this.potentialBidder?.organization_name || null,
          accountAddress: this.potentialBidder?.address || null,
          contactFirstName: this.form.contactFirstName,
          contactLastName: this.form.contactLastName,
          contactPhone: this.form.contactPhone,
          contactEmail: this.form.contactEmail,
          contactFax: null,
          expirationDate: null,
          totalAmount: this.form.amount ? Number(this.form.amount) : null,
          currency: 'CAD',
          status: null,
          sourceEntity: 'potential_bidder',
          sourceId: this.potentialBidder.id,
        },
      })
        .then(() => {
          this.$toast.success(this.$t('quotes.manual-quote.create-success'));
          this.$emit('quote-created');
          this.closeDialog();
        })
        .catch(() => {
          this.$toast.error(this.$t('quotes.manual-quote.create-error'));
        });
    },
    onCancel() {
      this.closeDialog();
    },
    closeDialog() {
      this.resetForm();
      this.$refs.dialog.isModalOpen = false;
    },
    resetForm() {
      this.form = {
        name: null,
        amount: null,
        description: null,
        contactFirstName: null,
        contactLastName: null,
        contactEmail: null,
        contactPhone: null,
      };
      this.potentialBidder = null;
      this.editingQuote = null;
    },
  },
};
</script>

<style lang="scss">
.base-dialog__create-manual-quote-dialog {
  display: flex;
  flex-direction: column;
  height: auto;
  width: 600px;
  position: relative;

  .base-dialog__content {
    padding: 4px 24px 24px 24px !important;
    font-size: 14px;
    background: $color-white !important;
    flex: 1;
    max-height: 70vh;
    min-height: 0;
    overflow-y: auto;
  }

  .base-dialog__actions {
    flex-shrink: 0;
    padding: 16px 24px 24px;
    background: $color-white;
    border-top: 1px solid $color-border;
  }

  .close-icon__container {
    display: none;
  }
}

.create-manual-quote-dialog {
  &__body {
    display: flex;
    flex-direction: column;
  }

  &__section {
    padding-bottom: 16px;
  }

  &__subtitle {
    font-size: 16px;
    color: $color-normal;
    font-weight: 600;
    padding-bottom: 8px;
  }

  &__row {
    display: flex;
    gap: 16px;

    .form-input {
      flex: 1;
    }
  }

  &__actions {
    justify-content: flex-end;
    display: flex;
  }

  &__cancel {
    margin-right: 12px;
  }
}
</style>
